import type { ReactNode } from "react";
import { NavLink } from "react-router-dom";
import { motion } from "motion/react";
import {
  Bot,
  Activity,
  Briefcase,
  CreditCard,
  FileSignature,
  IdCard,
  LogOut,
  Mail,
  ScrollText,
  Send,
  ShieldAlert,
  ShieldCheck,
  Users,
  KeyRound,
  Flag,
} from "lucide-react";
import { useAdminApi } from "../lib/api";
import { LOCALE_NAMES, locales, useI18n } from "../i18n";
import type { Locale } from "../i18n";
import type { AdminSession } from "../App";

/** 管理後台外框。用與前台相同的 neumorphic 語彙，但固定深色。 */
export default function Shell({
  session,
  onSignOut,
  children,
}: {
  session: AdminSession;
  onSignOut: () => void;
  children: ReactNode;
}) {
  const { t, locale, setLocale } = useI18n();

  /*
    待處理的檢舉數。

    放在側邊欄而不是總覽頁：檢舉是唯一一種「越晚看代價越大」的
    待辦 —— 一個詐騙帳號多存在一天，就多騙一個人。
    要管理員先點進某一頁才知道有幾件，那個延遲是真的成本。
  */
  const reportCount = useAdminApi<{ pending: number }>("/reports/pending");
  const pendingReports = reportCount.data?.pending ?? 0;

  /*
    訊號的數字和檢舉分開算。

    合成一個數字的話，管理員看到「7」不知道要去哪一頁 ——
    而那兩頁要做的判斷不一樣：檢舉要判斷指控是否成立，
    訊號要判斷規則有沒有誤判。
  */
  const fraudCount = useAdminApi<{ pending: number }>("/fraud-flags/pending");
  const pendingFraud = fraudCount.data?.pending ?? 0;

  const nav = [
    { to: "/", label: t("nav.overview"), icon: Activity },
    { to: "/members", label: t("nav.members"), icon: Users },
    { to: "/identity", label: t("nav.identity"), icon: IdCard },
    { to: "/reports", label: t("nav.reports"), icon: Flag, badge: pendingReports },
    { to: "/fraud", label: t("nav.fraud"), icon: ShieldAlert, badge: pendingFraud },
    { to: "/jobs", label: t("nav.jobs"), icon: Briefcase },
    { to: "/contracts", label: t("nav.contracts"), icon: FileSignature },
    { to: "/audit", label: t("nav.audit"), icon: ScrollText },
    { to: "/emails", label: t("nav.emails"), icon: Mail },
    { to: "/settings/email", label: t("nav.settingsEmail"), icon: Send },
    /*
      AI 供應商接在郵件後面。兩者都是「平台對外接的東西」，
      而且都是那種平常不會打開、壞掉才會想起來的頁面。
    */
    { to: "/settings/ai", label: t("nav.settingsAi"), icon: Bot },
    { to: "/settings/payments", label: t("nav.settingsPayments"), icon: CreditCard },
    { to: "/settings/account", label: t("nav.settingsAccount"), icon: KeyRound },
  ];

  const security = useAdminApi<{
    allowlistOpen: boolean;
    yourIp: string;
  }>("/security");

  async function signOut() {
    await fetch("/api/auth/logout", { method: "POST", credentials: "same-origin" });
    onSignOut();
  }

  return (
    <div className="min-h-screen bg-page p-3 sm:p-6">
      <div className="mx-auto flex min-h-[calc(100vh-1.5rem)] max-w-[1500px] overflow-hidden rounded-[2rem] border-[5px] border-white/8 nm-lg sm:min-h-[calc(100vh-3rem)]">
        <aside className="hidden w-60 shrink-0 flex-col bg-surface px-5 py-7 lg:flex">
          <div className="flex items-center gap-2.5 px-2">
            <span className="flex size-9 items-center justify-center rounded-xl nm-inset text-brand-400">
              <ShieldCheck className="size-4" />
            </span>
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-ink">{t("shell.title")}</p>
              <p className="truncate text-[10px] text-ink-muted">{session.role}</p>
            </div>
          </div>

          <nav className="mt-10 space-y-2">
            {nav.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                className={({ isActive }) =>
                  "relative flex items-center gap-3 rounded-2xl px-3.5 py-3 text-sm transition-colors " +
                  (isActive ? "font-semibold text-ink" : "font-medium text-ink-soft hover:text-ink")
                }
              >
                {({ isActive }) => (
                  <>
                    {isActive && (
                      <motion.span
                        layoutId="admin-nav"
                        className="absolute inset-0 -z-10 rounded-2xl nm"
                        aria-hidden
                      />
                    )}
                    <span className="flex size-8 items-center justify-center rounded-lg nm-inset-sm text-ink-muted">
                      <item.icon className="size-4" />
                    </span>
                    {item.label}
                    {/*
                      待處理的數字。只在大於 0 時出現 ——
                      一個常駐的「0」會被眼睛學會忽略，
                      等到它變成 3 的時候也一起忽略掉了。
                    */}
                    {"badge" in item && (item.badge ?? 0) > 0 && (
                      <span className="ml-auto flex min-w-5 items-center justify-center rounded-full bg-brand-400/20 px-1.5 text-[11px] font-semibold text-brand-300">
                        {item.badge}
                      </span>
                    )}
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          <button
            type="button"
            onClick={signOut}
            className="mt-auto flex items-center gap-3 rounded-2xl nm-pressable px-3.5 py-3 text-sm font-medium text-ink-soft hover:text-ink"
          >
            <span className="flex size-8 items-center justify-center rounded-lg nm-inset-sm text-ink-muted">
              <LogOut className="size-4" />
            </span>
            {t("shell.logout")}
          </button>
        </aside>

        <div className="flex min-w-0 flex-1 flex-col">
          <header className="flex shrink-0 items-center justify-between gap-4 border-b border-line px-6 py-5">
            <h1 className="text-lg font-semibold text-ink">{t("shell.brand")}</h1>
            {/*
              語言切換。

              十八本字典原本沒有任何入口 —— i18n 建好了、
              LOCALE_NAMES 匯出了，但沒有一個畫面用到 setLocale。
              管理員只能靠瀏覽器語言碰運氣，或自己去改 localStorage。

              用原生的 <select> 而不是自刻的下拉：十八個選項、
              內部工具、鍵盤操作與螢幕閱讀器的行為全部免費拿到。
              前台那個自刻的選單有存在的理由（要放國旗、要能做轉場），
              這裡沒有。

              名字一律用該語言自己的說法 —— 一個只看得懂泰文的
              審核人員，在一串中文語言名裡找不到「ไทย」。
            */}
            <span className="flex items-center gap-2.5">
              <label className="sr-only" htmlFor="admin-locale">
                {t("shell.language")}
              </label>
              <select
                id="admin-locale"
                value={locale}
                onChange={(e) => setLocale(e.target.value as Locale)}
                className="rounded-xl nm-inset-sm bg-transparent px-2.5 py-1.5 text-xs text-ink-soft outline-none"
              >
                {locales.map((code) => (
                  <option key={code} value={code} className="bg-page text-ink">
                    {LOCALE_NAMES[code]}
                  </option>
                ))}
              </select>
              <span className="flex size-9 items-center justify-center rounded-full bg-gradient-to-br from-brand-700 to-brand-950 text-[10px] font-semibold text-white">
                {session.displayName.slice(0, 2)}
              </span>
              <span className="hidden text-sm text-ink sm:block">{session.displayName}</span>
            </span>
          </header>

          <div className="min-h-0 flex-1 overflow-y-auto p-6">
            {/*
              白名單被關掉時的警示。刻意做得無法忽略 ——
              「暫時關掉」的最大風險是關了之後忘記開回來。
            */}
            {/*
              安全設定被關掉時的警示。
              「暫時關掉」最大的風險是關了之後忘記開回來 ——
              靜靜地放行等於幫忙隱瞞這件事，所以做得無法忽略。
            */}
            {security.data?.allowlistOpen && (
              <motion.div
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-6 flex items-start gap-3 rounded-2xl nm-inset px-5 py-4"
              >
                <ShieldAlert className="mt-0.5 size-5 shrink-0 text-brand-300" />
                <div className="min-w-0 text-sm leading-relaxed">
                  <p className="font-semibold text-ink">{t("shell.securityWarn")}</p>

                  <ul className="mt-2 space-y-2">
                    {security.data.allowlistOpen && (
                      <li className="text-ink-soft">
                        <strong className="text-ink">{t("shell.allowlistOpen")}</strong>{" "}
                        {t("shell.allowlistOpenBody")}
                        <code className="ml-1 rounded-lg nm-sm px-2 py-0.5 text-[11px]">
                          npm run admin:allow-ip
                        </code>
                      </li>
                    )}
                  </ul>

                  <p className="mt-2.5 text-[11px] text-ink-muted">
                    {t("shell.yourIp", { ip: security.data.yourIp })}
                  </p>
                </div>
              </motion.div>
            )}
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}
